<script setup>
import { computed, inject } from 'vue';
import { Modal, Button } from 'ant-design-vue';

const props = defineProps({
    open: { type: Boolean, default: false },
    record: { type: Object, default: null }
});

const emit = defineEmits(['update:open']);

const ve = inject('versionEditor', null);

const visible = computed({
    get: () => props.open,
    set: (v) => emit('update:open', v)
});

const formatViewFieldLabel = (name) => {
    const s = String(name ?? '').trim();
    if (!s) return '欄位';
    return s.replace(/\?+$/u, '').trim() || s;
};

const isAsset = computed(() => {
    if (!ve?.typeIdToClass || !props.record) return true;
    return ve.typeIdToClass[Number(props.record.type_id)] === 0;
});

const categoryLabel = computed(() => {
    const r = props.record;
    if (!r || !ve) return '—';
    const type = ve.getTypeNameById(r.type_id);
    const opt =
        r.option_name ||
        ve.getOptionName(r.type_id, r.option_id, '') ||
        '';
    return opt && opt !== type ? `${type} · ${opt}` : type || '—';
});

const detailRows = computed(() => {
    const fields = props.record?.fields;
    if (!Array.isArray(fields) || !fields.length) return [];
    if (ve?.renderFieldChips) {
        return ve.renderFieldChips(fields).map((chip) => {
            const sep = chip.includes(': ') ? ': ' : ' ';
            const i = chip.includes(': ')
                ? chip.indexOf(': ')
                : chip.indexOf(' ');
            if (i === -1) {
                return { label: formatViewFieldLabel(chip), value: '—' };
            }
            return {
                label: formatViewFieldLabel(chip.slice(0, i)),
                value: chip.slice(i + sep.length).trim() || '—'
            };
        });
    }
    return fields.map((f) => ({
        label: formatViewFieldLabel(f.field_name ?? f.name ?? f.field_id),
        value: String(f.value ?? f.detail_value ?? '—')
    }));
});

const formatMoney = (n) =>
    ve?.formatCurrency ? ve.formatCurrency(n) : String(n ?? '—');
</script>

<template>
    <Modal
        v-model:open="visible"
        wrap-class-name="edit-modal view-item-modal"
        :footer="null"
        :destroy-on-close="true"
        :mask-closable="true"
        :width="560"
    >
        <template #title>
            <div class="view-item-modal__title-wrap">
                <span class="view-item-modal__title">資料內容</span>
                <span v-if="record" class="view-item-modal__subtitle">
                    <span
                        class="view-item-modal__type-pill"
                        :class="
                            isAsset
                                ? 'view-item-modal__type-pill--asset'
                                : 'view-item-modal__type-pill--liability'
                        "
                    >
                        {{ isAsset ? '資產' : '負債' }}
                    </span>
                    {{ categoryLabel }}
                    <template v-if="record.name"> · {{ record.name }}</template>
                </span>
            </div>
        </template>

        <div v-if="record" class="view-item-modal__body">
            <section class="view-item-modal__section">
                <h4 class="view-item-modal__section-title">基本資訊</h4>
                <dl class="view-item-modal__rows">
                    <div class="view-item-modal__row">
                        <dt>類別</dt>
                        <dd>{{ categoryLabel }}</dd>
                    </div>
                    <div class="view-item-modal__row">
                        <dt>名稱</dt>
                        <dd>{{ record.name || '—' }}</dd>
                    </div>
                </dl>
                <div class="view-item-modal__highlights">
                    <div
                        class="view-item-modal__highlight"
                        :class="
                            isAsset
                                ? 'view-item-modal__highlight--asset'
                                : 'view-item-modal__highlight--liability'
                        "
                    >
                        <span class="view-item-modal__highlight-label">
                            {{ isAsset ? '市值（現值）' : '餘額' }}
                        </span>
                        <span class="view-item-modal__highlight-value">
                            {{ formatMoney(record.amount) }}
                        </span>
                    </div>
                    <div
                        class="view-item-modal__highlight"
                        :class="
                            isAsset
                                ? 'view-item-modal__highlight--income'
                                : 'view-item-modal__highlight--expense'
                        "
                    >
                        <span class="view-item-modal__highlight-label">
                            {{ isAsset ? '月收入' : '月付金' }}
                        </span>
                        <span class="view-item-modal__highlight-value">
                            {{ formatMoney(record.monthly_interest_expense) }}
                        </span>
                    </div>
                </div>
            </section>

            <section class="view-item-modal__section">
                <h4 class="view-item-modal__section-title">詳細資訊</h4>
                <dl
                    v-if="detailRows.length"
                    class="view-item-modal__rows view-item-modal__rows--detail"
                >
                    <div
                        v-for="(row, idx) in detailRows"
                        :key="idx"
                        class="view-item-modal__row"
                    >
                        <dt>{{ row.label }}</dt>
                        <dd>{{ row.value }}</dd>
                    </div>
                </dl>
                <p v-else class="view-item-modal__empty">無額外詳細資訊</p>
            </section>

            <div class="view-item-modal__footer">
                <Button type="primary" block size="large" @click="visible = false">
                    關閉
                </Button>
            </div>
        </div>
    </Modal>
</template>

<style scoped>
.view-item-modal__title-wrap {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-right: 24px;
}
.view-item-modal__title {
    font-size: 18px;
    font-weight: 700;
    color: #272b36;
}
.view-item-modal__subtitle {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #565962;
    line-height: 1.4;
}
.view-item-modal__type-pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
}
.view-item-modal__type-pill--asset {
    background: #e8faf3;
    color: #1e4d3f;
}
.view-item-modal__type-pill--liability {
    background: #fff1f0;
    color: #a8071a;
}
.view-item-modal__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-size: 15px;
}
.view-item-modal__section {
    border: 1px solid #e8ecf0;
    border-radius: 12px;
    padding: 14px 16px;
    background: #fafbfc;
}
.view-item-modal__section-title {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 700;
    color: #272b36;
    letter-spacing: 0.02em;
}
.view-item-modal__rows {
    margin: 0;
}
.view-item-modal__row {
    display: grid;
    grid-template-columns: 88px 1fr;
    gap: 12px;
    align-items: baseline;
    padding: 8px 0;
    border-bottom: 1px solid #eef1f4;
}
.view-item-modal__row:last-child {
    border-bottom: none;
}
.view-item-modal__row dt {
    margin: 0;
    font-size: 14px;
    color: #8c8f96;
    font-weight: 500;
}
.view-item-modal__row dd {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #272b36;
    word-break: break-word;
}
.view-item-modal__rows--detail .view-item-modal__row dd {
    font-weight: 500;
}
.view-item-modal__highlights {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-top: 12px;
}
@media (max-width: 480px) {
    .view-item-modal__highlights {
        grid-template-columns: 1fr;
    }
}
.view-item-modal__highlight {
    border-radius: 10px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.view-item-modal__highlight-label {
    font-size: 13px;
    color: #8c8f96;
    font-weight: 500;
}
.view-item-modal__highlight-value {
    font-size: 20px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
}
.view-item-modal__highlight--asset {
    background: #f0f7ff;
    border: 1px solid #c5dcfa;
}
.view-item-modal__highlight--asset .view-item-modal__highlight-value {
    color: #1677ff;
}
.view-item-modal__highlight--liability {
    background: #fff5f5;
    border: 1px solid #f5c4c4;
}
.view-item-modal__highlight--liability .view-item-modal__highlight-value {
    color: #cf1322;
}
.view-item-modal__highlight--income {
    background: #f3fbf6;
    border: 1px solid #b8e6c8;
}
.view-item-modal__highlight--income .view-item-modal__highlight-value {
    color: #389e0d;
}
.view-item-modal__highlight--expense {
    background: #fff8f0;
    border: 1px solid #f5d9b8;
}
.view-item-modal__highlight--expense .view-item-modal__highlight-value {
    color: #d46b08;
}
.view-item-modal__empty {
    margin: 0;
    padding: 16px 0;
    text-align: center;
    color: #a8adb6;
    font-size: 14px;
}
.view-item-modal__footer {
    padding-top: 4px;
}
</style>
